import React, { useState, useEffect } from 'react';
import { Mail, Send, Users, History, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { apiClient } from '../../services/api/apiClient'; // Fixed import
import { useAuth } from '../../contexts/AuthContext';

/**
 * Trang Email Marketing Automation (Admin)
 */
const EmailMarketing: React.FC = () => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState<'compose' | 'history'>('compose');
    const [loading, setLoading] = useState(false);

    // Compose State
    const [subject, setSubject] = useState('');
    const [content, setContent] = useState('');
    const [targetRole, setTargetRole] = useState('all');
    const [testEmail, setTestEmail] = useState('');
    const [specificEmail, setSpecificEmail] = useState('');

    // History State
    const [campaigns, setCampaigns] = useState<any[]>([]);

    useEffect(() => {
        if (activeTab === 'history') {
            loadCampaigns();
        }
    }, [activeTab]);

    const loadCampaigns = async () => {
        try {
            const res = await apiClient.get<any[]>('/email-marketing/campaigns');
            if (res.data) {
                setCampaigns(res.data);
            } else {
                toast.error('Không thể tải lịch sử chiến dịch');
            }
        } catch (error) {
            console.error(error);
            toast.error('Lỗi kết nối');
        }
    };

    const handleSendTest = async () => {
        if (!testEmail) {
            toast.error('Vui lòng nhập email để test');
            return;
        }
        try {
            setLoading(true);
            const res = await apiClient.post('/email-marketing/test-config?email=' + testEmail);
            if (res.error) throw new Error(res.error);

            toast.success('Email test đã được gửi!');
        } catch (error: any) {
            toast.error(error.message || 'Gửi thất bại. Kiểm tra cấu hình SMTP server.');
        } finally {
            setLoading(false);
        }
    };

    const handleSendCampaign = async () => {
        if (!subject || !content) {
            toast.error('Vui lòng nhập tiêu đề và nội dung');
            return;
        }

        if (!confirm(`Bạn có chắc muốn gửi email này tới nhóm: ${targetRole}?`)) return;

        try {
            setLoading(true);
            const res = await apiClient.post('/email-marketing/send', {
                subject,
                content,
                target_role: targetRole === 'specific' ? undefined : targetRole,
                target_emails: targetRole === 'specific' ? [specificEmail] : []
            });

            if (res.error) throw new Error(res.error);

            toast.success('Chiến dịch đã được khởi tạo và đang gửi ngầm!');
            setSubject('');
            setContent('');
            setActiveTab('history');
        } catch (error: any) {
            toast.error(error.message || 'Gửi chiến dịch thất bại');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <Mail className="w-8 h-8 text-blue-600" />
                        Email Automation
                    </h1>
                    <p className="text-gray-600 mt-1">Gửi thông báo và marketing tới người dùng</p>
                </div>

                <div className="flex bg-gray-100 p-1 rounded-lg">
                    <button
                        onClick={() => setActiveTab('compose')}
                        className={`px-4 py-2 rounded-md font-medium transition ${activeTab === 'compose' ? 'bg-white shadow text-blue-600' : 'text-gray-600 hover:text-gray-900'
                            }`}
                    >
                        Soạn thảo
                    </button>
                    <button
                        onClick={() => setActiveTab('history')}
                        className={`px-4 py-2 rounded-md font-medium transition ${activeTab === 'history' ? 'bg-white shadow text-blue-600' : 'text-gray-600 hover:text-gray-900'
                            }`}
                    >
                        Lịch sử
                    </button>
                </div>
            </div>

            {/* Content */}
            {activeTab === 'compose' ? (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Editor Column */}
                    <div className="lg:col-span-2 space-y-6">
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Tiêu đề Email</label>
                                <input
                                    type="text"
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    placeholder="Ví dụ: Ưu đãi đặc biệt tháng này..."
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Nội dung (HTML)
                                    <span className="text-xs font-normal text-gray-500 ml-2">Hỗ trợ biến: {'{name}'}, {'{email}'}</span>
                                </label>
                                <textarea
                                    value={content}
                                    onChange={(e) => setContent(e.target.value)}
                                    rows={15}
                                    placeholder="<p>Xin chào {name},</p><p>Chúng tôi có thông báo mới...</p>"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-mono text-sm"
                                ></textarea>
                                <p className="text-xs text-gray-400 mt-1">Khuyên dùng: Viết HTML cơ bản để tối ưu hiển thị email.</p>
                            </div>
                        </div>
                    </div>

                    {/* Settings Column */}
                    <div className="space-y-6">
                        {/* Target Select */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <Users className="w-5 h-5 text-gray-500" />
                                Người nhận
                            </h3>
                            <div className="space-y-3">
                                <label className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                                    <input
                                        type="radio"
                                        name="target"
                                        value="all"
                                        checked={targetRole === 'all'}
                                        onChange={(e) => setTargetRole(e.target.value)}
                                        className="h-4 w-4 text-blue-600"
                                    />
                                    <div>
                                        <span className="block font-medium">Tất cả người dùng</span>
                                        <span className="text-xs text-gray-500">Gửi cho toàn bộ thành viên</span>
                                    </div>
                                </label>
                                <label className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                                    <input
                                        type="radio"
                                        name="target"
                                        value="student"
                                        checked={targetRole === 'student'}
                                        onChange={(e) => setTargetRole(e.target.value)}
                                        className="h-4 w-4 text-blue-600"
                                    />
                                    <div>
                                        <span className="block font-medium">Học sinh</span>
                                        <span className="text-xs text-gray-500">Chỉ gửi cho tài khoản học sinh</span>
                                    </div>
                                </label>
                                <label className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                                    <input
                                        type="radio"
                                        name="target"
                                        value="teacher"
                                        checked={targetRole === 'teacher'}
                                        onChange={(e) => setTargetRole(e.target.value)}
                                        className="h-4 w-4 text-blue-600"
                                    />
                                    <div>
                                        <span className="block font-medium">Giáo viên</span>
                                        <span className="text-xs text-gray-500">Chỉ gửi cho tài khoản giáo viên</span>
                                    </div>
                                </label>
                                <label className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                                    <input
                                        type="radio"
                                        name="target"
                                        value="specific"
                                        checked={targetRole === 'specific'}
                                        onChange={(e) => setTargetRole(e.target.value)}
                                        className="h-4 w-4 text-blue-600"
                                    />
                                    <div>
                                        <span className="block font-medium">Cụ thể</span>
                                        <span className="text-xs text-gray-500">Gửi tới 1 email xác định</span>
                                    </div>
                                </label>

                                {targetRole === 'specific' && (
                                    <div className="ml-7 mt-2">
                                        <input
                                            type="email"
                                            value={specificEmail}
                                            onChange={(e) => setSpecificEmail(e.target.value)}
                                            placeholder="nhap.email@example.com"
                                            className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-1 focus:ring-blue-500"
                                        />
                                    </div>
                                )}
                            </div>

                            <button
                                onClick={handleSendCampaign}
                                disabled={loading}
                                className="w-full mt-6 flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium transition disabled:opacity-50"
                            >
                                <Send className="w-4 h-4" />
                                <span>{loading ? 'Đang gửi...' : 'Gửi Chiến Dịch'}</span>
                            </button>
                        </div>

                        {/* Test Email */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <AlertCircle className="w-5 h-5 text-gray-500" />
                                Test Gửi Email
                            </h3>
                            <div className="space-y-3">
                                <input
                                    type="email"
                                    value={testEmail}
                                    onChange={(e) => setTestEmail(e.target.value)}
                                    placeholder="Nhập email của bạn..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                />
                                <button
                                    onClick={handleSendTest}
                                    disabled={loading}
                                    className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 rounded-lg text-sm font-medium transition"
                                >
                                    Gửi Test
                                </button>
                                <p className="text-xs text-gray-500 italic">
                                    Lưu ý: Bạn cần cấu hình SMTP trong file .env ở Backend thì mới gửi được.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 border-b border-gray-100">
                            <tr>
                                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Ngày tạo</th>
                                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Tiêu đề</th>
                                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Đối tượng</th>
                                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase text-right">Số lượng</th>
                                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Trạng thái</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {campaigns.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                                        Chưa có chiến dịch nào
                                    </td>
                                </tr>
                            ) : (
                                campaigns.map((camp) => (
                                    <tr key={camp.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm text-gray-600">
                                            {new Date(camp.created_at).toLocaleString('vi-VN')}
                                        </td>
                                        <td className="px-6 py-4 font-medium text-gray-900">
                                            {camp.subject}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600">
                                            {camp.target_role === 'all' ? 'Tất cả' : camp.target_role}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-900 text-right">
                                            {camp.total_recipients}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                {camp.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default EmailMarketing;
